






























































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import BerkasRmService, {
  JenisBerkasOption
} from "@/services/BerkasRmService";
import { appModule } from "@/store/modules/app";
import DataRiwayat from "@/services/DataRiwayat";

const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const OPSI_LEVEL_PASIEN = {
  value: "",
  text: "Level pasien (tanpa kunjungan)"
};

interface UploadForm {
  jenis_berkas_id: number | null;
  register_id: string;
  file: File | null;
  keterangan: string;
}

interface RegisterOption {
  value: string;
  text: string;
}

/** Baris dari GET /pasien/{rm}/register (PasienController@getListRegister). */
interface RegisterRow {
  register: string | number;
  masuk: string;
  unit?: { id: string; nama: string };
}

@Component
export default class DialogUploadBerkas extends Vue {
  @Prop({ default: false }) value!: boolean;
  @Prop({ required: true }) rm!: string;
  @Prop({ default: "" }) register!: string;
  @Prop({ default: "" }) defaultJenisKode!: string;
  @Prop({ type: String, default: "" }) subJenisKode!: string;
  @Prop({ type: Boolean, default: false }) lockJenis!: boolean;
  @Prop({ type: String, default: "" }) sumberTipe!: string;
  @Prop({ type: [Number, String], default: null }) sumberId!:
    | number
    | string
    | null;

  valid = false;
  uploading = false;
  loadingJenis = false;
  loadingRegister = false;
  jenisBerkasOptions: JenisBerkasOption[] = [];
  registerOptions: RegisterOption[] = [OPSI_LEVEL_PASIEN];
  registerLoadedFor = "";

  form: UploadForm = {
    jenis_berkas_id: null,
    register_id: "",
    file: null,
    keterangan: ""
  };

  get dialog(): boolean {
    return this.value;
  }

  set dialog(val: boolean) {
    this.$emit("input", val);
  }

  get canSubmit(): boolean {
    return (
      !!this.form.file &&
      this.form.jenis_berkas_id !== null &&
      !this.uploading
    );
  }

  requiredRule = (v: unknown) => !!v || "Wajib diisi.";

  keteranganRule = (v: string) =>
    !v || v.length <= 500 || "Maksimal 500 karakter.";

  fileRules = [
    (v: File | null) => !!v || "File wajib dipilih.",
    (v: File | null) =>
      !v || v.size <= MAX_SIZE_BYTES || "Ukuran file maksimal 10 MB.",
    (v: File | null) => {
      if (!v) return true;
      const allowed = ["application/pdf", "image/jpeg", "image/png"];
      return (
        allowed.includes(v.type) ||
        "Format tidak valid. Hanya PDF/JPG/PNG yang diterima."
      );
    }
  ];

  async created() {
    await this.loadJenisBerkas();
  }

  async loadJenisBerkas() {
    this.loadingJenis = true;
    try {
      this.jenisBerkasOptions = await BerkasRmService.listJenisBerkas();
      this.applyDefaultJenis();
    } catch (e) {
      // error sudah di-handle oleh ErrorAlert di service.
    } finally {
      this.loadingJenis = false;
    }
  }

  get rmKey(): string {
    const v = String(this.rm || "");
    return v.length > 6 ? v.slice(-6) : v;
  }

  get sumberAktif(): boolean {
    return !!this.sumberTipe && Number(this.sumberId) > 0;
  }

  get registerTerkunci(): boolean {
    return this.sumberAktif && !!this.register;
  }

  get kategoriTerpilih(): JenisBerkasOption | null {
    const kandidat = [this.subJenisKode, this.defaultJenisKode];

    for (const kode of kandidat) {
      if (!kode) continue;
      const opsi = this.jenisBerkasOptions.find(o => o.kode === kode);
      if (opsi) return opsi;
    }

    return null;
  }

  get jenisTerkunci(): boolean {
    return this.lockJenis && !!this.kategoriTerpilih;
  }

  applyDefaultJenis() {
    if (this.form.jenis_berkas_id !== null) return;
    const opsi = this.kategoriTerpilih;
    if (opsi) this.form.jenis_berkas_id = opsi.id;
  }

  async loadRegister() {
    if (!this.rm || this.registerLoadedFor === this.rm) return;
    this.loadingRegister = true;
    try {
      const resp = await DataRiwayat.getDataRiwayatRm(this.rm, "");
      // DataRiwayat menelan error dan mengembalikan body error — jangan di-map.
      const raw = resp && resp.data;
      const list: RegisterRow[] = Array.isArray(raw) ? raw : [];
      this.registerOptions = [OPSI_LEVEL_PASIEN].concat(
        list.map((r: RegisterRow) => ({
          value: String(r.register),
          text: `${this.tanggal(r.masuk)} — ${
            (r.unit && r.unit.nama) || "-"
          } (No. ${r.register})`
        }))
      );
      this.registerLoadedFor = this.rm;
    } catch (e) {
      // error sudah di-handle oleh snack alert di service.
    } finally {
      this.loadingRegister = false;
    }
  }

  tanggal(iso: string): string {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  @Watch("value")
  onToggle(val: boolean) {
    if (val) {
      this.resetForm();
      // Refresh tiap dialog dibuka supaya kategori yang baru dibuat ikut muncul.
      this.loadJenisBerkas();
      this.loadRegister();
    }
  }

  resetForm() {
    this.form = {
      jenis_berkas_id: null,
      register_id: this.register ? String(this.register) : "",
      file: null,
      keterangan: ""
    };
    const formEl = this.$refs.form as { resetValidation?: () => void } | null;
    formEl?.resetValidation?.();
  }

  async submit() {
    const formEl = this.$refs.form as { validate?: () => boolean } | null;
    if (formEl?.validate && !formEl.validate()) return;
    if (!this.form.file || this.form.jenis_berkas_id === null) return;

    this.uploading = true;
    try {
      const data = new FormData();
      data.append("file", this.form.file);
      data.append("jenis_berkas_id", String(this.form.jenis_berkas_id));
      if (this.form.register_id) {
        data.append("register_id", this.form.register_id);
      }
      if (this.form.keterangan) {
        data.append("keterangan", this.form.keterangan);
      }
      if (this.sumberAktif) {
        data.append("sumber_tipe", this.sumberTipe);
        data.append("sumber_id", String(this.sumberId));
      }

      const created = await BerkasRmService.upload(this.rmKey, data);
      this.$emit("uploaded", created);
      appModule.NoticeDelay("Berhasil mengunggah berkas");
      this.uploading = false;
      this.dialog = false;
    } catch (e) {
      // error notification sudah ditampilkan oleh ErrorAlert di service.
      this.uploading = false;
    }
  }

  close() {
    if (this.uploading) return;
    this.dialog = false;
  }

  onOutsideClick() {
    if (!this.uploading) this.close();
  }
}
